import * as Sentry from '@sentry/browser'
import Cookies from 'js-cookie'
import ky, { type HTTPError } from 'ky'

export * from './api-client'
export * from './identity-api'
export * from './query-keys'
export * from './pagination'
export * from './types'

export function getApiErrorMessage(error: unknown) {
    if (
        typeof error === 'object' &&
        error !== null &&
        'error' in error &&
        typeof error.error === 'string'
    ) {
        return error.error
    }

    if (error instanceof Error) {
        return error.message
    }

    return 'An error occurred'
}

export function getNextPageParam(next: string | null, fetchLimit: number) {
    if (!next) {
        return
    }

    const url = new URL(next)
    const searchParams = url.searchParams
    const offset = parseInt(searchParams.get('offset') ?? '')
    if (isNaN(offset)) {
        return
    }

    return Math.ceil(offset / fetchLimit)
}

export const apiClient = ky.create({
    prefixUrl: '/endpoints',
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json',
    },
    retry: 0, // will be handled by react-query
    hooks: {
        beforeRequest: [
            (request) => {
                const csrfToken = Cookies.get('csrftoken')
                if (csrfToken) {
                    request.headers.set('X-CSRFToken', csrfToken)
                }
            },
        ],
        afterResponse: [
            (request, _options, response) => {
                if (response.status < 400) {
                    return response
                }

                Sentry.withScope((scope) => {
                    scope.setExtra('request', {
                        url: request.url,
                        method: request.method,
                        body: request.body,
                        headers: Object.fromEntries(request.headers.entries()),
                    })
                    scope.setExtra('response', {
                        status: response.status,
                        headers: Object.fromEntries(response.headers.entries()),
                    })
                })
            },
        ],
    },
})

export type ApiError<Error = unknown> = HTTPError<{
    error: Error
    request_id: string
}>
